import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';

// Material UI
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { TextareaAutosize } from '@mui/base/TextareaAutosize';

import { APIErrorResponse, GeneNameRecord, checkGeneName } from '../../api/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faDna, faXmarkCircle } from '@fortawesome/free-solid-svg-icons';
import { CircularProgress, Divider, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import SearchButton from './SearchButton';
import { NamedSpecies, Species } from '../../Types';

// Define our Supported Species here.
const SUPPORTED_SPECIES : Array<NamedSpecies> = [
  {name:"Homo sapiens (human)", species: Species.HUMAN},
  {name:"Mus musculus (house mouse)", species: Species.MOUSE},
  {name:"Rattus norvegicus (Norway rat)", species: Species.RAT},
  {name:"Macaca mulatta (Rhesus monkey)", species: Species.MM},
  {name:"Macaca fascicularis (crab-eating macaque)", species: Species.MF}
];

// Dynamic variables need to be pulled out here.
let validationTimeoutID: ReturnType<typeof setTimeout> | null = null;

const SearchForm = () => {
  // Used to hold the actual search data
  const [selectedSpeciesIndex, setSelectedSpeciesIndex] = React.useState<string>('');
  const [selectedAccessionIndex, setSelectedAccessionIndex] = React.useState<string>('');
  
  // State variables used to validate and store the Accession Number
  const [accessionNumber, setAccessionNumber] = useState<string>("");

  const [disallowedCharacterInCustomSequence, setDisallowedCharacterInCustomSequence] = React.useState<boolean>(false);

  // State variables used to validate and store the Gene Name
  const [geneName, setGeneName] = useState<string>("");
  const [geneNameValid, setGeneNameValid] = useState<boolean>(false);
  const [validatingGeneName, setValidatingGeneName] = useState<boolean>(false);
  const [geneData, setGeneData] = useState<GeneNameRecord | null>(null);

  // Used for confirming each step of the gene entry process
  const [customGeneSequence, setCustomGeneSequence] = useState<string>("");

  const VALIDATE_ACCESSION_COUNTDOWN : number = 1000; // Wait 150ms between keypresses when validating accession number.

  // Memoized confirmations and use effects to clear the form
  const confirmedSpecies = useMemo(() => {
    return selectedSpeciesIndex !== ''
  }, [selectedSpeciesIndex]);

  useEffect(() => {
    setGeneName('');
    setAccessionNumber('');
    setSelectedAccessionIndex('');
    setCustomGeneSequence('');
  }, [selectedSpeciesIndex]);

  const confirmedGene = useMemo(() => {
    return !validatingGeneName && geneNameValid && geneName.length > 0
  }, [geneNameValid, geneName, validatingGeneName]);

  useEffect(() => {
    setAccessionNumber('');
    setSelectedAccessionIndex('');
    setCustomGeneSequence('');
  }, [geneName]);

  const hasCustomAccessionNumber = useMemo(() => {
    return selectedAccessionIndex == '0'
  }, [selectedAccessionIndex]);

  const confirmedAccession = useMemo(() => {
    if (hasCustomAccessionNumber) {
      return (!disallowedCharacterInCustomSequence && customGeneSequence.length > 0);
    } else {
      return !!selectedAccessionIndex
    }
  }, [hasCustomAccessionNumber, disallowedCharacterInCustomSequence, customGeneSequence, selectedAccessionIndex]);

  
const MIN_SEQUENCE_LENGTH = 80;

const renderCustomSequenceTextArea = () => {
  const currentLength = customGeneSequence.length;
  const hasError =
    disallowedCharacterInCustomSequence || currentLength < MIN_SEQUENCE_LENGTH;

  const progress = Math.min((currentLength / MIN_SEQUENCE_LENGTH) * 100, 100);

  return (
    <Stack direction="column" spacing={1} marginTop={'4px'}>
      <Stack direction="column" spacing={0.5}>
        {/* Top row: helper text + counter */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            variant="body2"
            fontWeight="bold"
          >
            This field only supports the characters 'A' 'T' 'U' 'C' 'G'
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color:
                currentLength < MIN_SEQUENCE_LENGTH
                  ? "warning.main"
                  : hasError
                  ? "error.main"
                  : "success.main",
            }}
          >
            {currentLength < MIN_SEQUENCE_LENGTH
              ? `${MIN_SEQUENCE_LENGTH - currentLength} more characters needed`
              : `Length OK (${currentLength} chars)`}
          </Typography>
        </Stack>
        {/* Progress bar below */}
        {/* <LinearProgress
          variant="determinate"
          value={progress}
          color={hasError ? "error" : "primary"}
          sx={{
            height: 6,
            borderRadius: 2,
            mt: 0.5,
          }}
        /> */}
      </Stack>

      <TextareaAutosize
        placeholder="Enter Custom Sequence"
        minRows={12}
        maxRows={12}
        value={customGeneSequence}
        disabled={!confirmedGene}
        style={{
          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
          fontWeight: 500,
          fontSize: "1rem",
          lineHeight: 1.3,
          letterSpacing: ".01em",
          resize: "none",
          transition: "outline-color 0.25s cubic-bezier(0.22, 0.61, 0.36, 1)",
          outlineColor: hasError && currentLength > 0 ? "#d32f2f" : "#1876d2",
          borderRadius: "4px",
          padding: "8px",
        }}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
          const rawString = event.target.value;
          const filteredString = rawString.replace(/([^atucgATUCG]+)/gi, "");
          // const disallowedCharacter = filteredString.length !== rawString.length;
          // setDisallowedCharacterInCustomSequence(disallowedCharacter);
          setCustomGeneSequence(filteredString.toUpperCase());
        }}
      />
    </Stack>
  );
  };

  const getAccessionsForSelectedGene = () : Array<string> => {
    if (geneData && geneData.accessionRegions) {
      return geneData.accessionRegions;
    }
    return [];
  }

  const renderSelectSpecies = () => (
    <FormControl>
      {/* Species Select */}
      <InputLabel id="select-species-label">Select Species</InputLabel>
      <Select
        required={true}
        labelId="select-species-label"
        id="select-species"
        value={selectedSpeciesIndex}
        label="Select Species"
      >
        {SUPPORTED_SPECIES.map((item, index : number) => (
          <MenuItem value={index} key={index} onClick={() => {setSelectedSpeciesIndex(index.toString())}}>{item.name}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderSelectGene = () => (
    <Box style={{display:'flex'}}>
      <TextField
        label="Enter Gene Name or Alias"
        disabled={!confirmedSpecies}
        value={geneName}
        error={!geneNameValid && geneName.length > 0}
        style={{width: (geneName.length > 0 && confirmedSpecies) ? 'calc(100% - 48px)' : '100%', transition: "width 0.5s cubic-bezier(0.22, 0.61, 0.36, 1) 0s"}}
        onChange={(event : ChangeEvent<HTMLInputElement>) => {
          // Store a reference to the possible geneName
          let possibleGeneName : string = event.target.value;
          // Update the state of the stored gene name
          setGeneName(possibleGeneName);
          // On keypress, clear our timeout
          if (validationTimeoutID != null) {
            clearTimeout(validationTimeoutID);
          }
          // Indicate that we are in the process of validating the gene name
          setValidatingGeneName(true);
          setGeneNameValid(true);
          // If there is a value for the gene name
          if (possibleGeneName.length > 0) {
            // Now that there is no timeout running, enqueue a timeout.
            // This is to prevent API spam on the client side, we do server-side rate limiting too.
            validationTimeoutID = setTimeout(() => {
              // Check that the gene name that the user is specifying is valid.
              checkGeneName(possibleGeneName, SUPPORTED_SPECIES[parseInt(selectedSpeciesIndex) || 0].species).then((data : GeneNameRecord | APIErrorResponse) => {
                // We have found a valid Gene Name
                setGeneData(data as GeneNameRecord);
                // Set that this accession number is valid
                setGeneNameValid(true);
              }).catch((error) => {
                // We dont know the gene name, but it is invalid
                console.log(error);
                // Set that this gene name is not valid
                setGeneNameValid(false);
              }).finally(() => {
                // We are done validating the gene name
                setValidatingGeneName(false);
              });
            }, VALIDATE_ACCESSION_COUNTDOWN);
          } else {
            // String is empty, so clear the accession number
            
          }
        }}
      />
      {/* Render the Valid SVG if this is valid */}
      <div style={{width: (geneName.length > 0 && confirmedSpecies) ? '48px' : '0px', height:"56px", overflow:'hidden', transform:"translateY(8px)", transition: "width 0.5s cubic-bezier(0.22, 0.61, 0.36, 1) 0s"}}>
        <div>
          {/* Render the spinner */}
          {validatingGeneName ? <CircularProgress sx={{position:'absolute', left:'8px'}}/> : <></>}
          {/* Render the validation, either a green check or a red X */}
          <Tooltip title={geneNameValid ? (geneName + " is a valid Gene.") : ("Our databases are unfamiliar with the Gene name or Alias you have entered.")}>
            {validatingGeneName ? <></> : <FontAwesomeIcon icon={geneNameValid ? faCheckCircle : faXmarkCircle} color={geneNameValid ? "#1da653" : "#d32f2f"} style={{width:'36px', height:"36px", transform:'translate(2px, 2px)'}}/>}
          </Tooltip>
        </div>
      </div>
    </Box>
  );

  const renderSelectAccessionNumber = () => (
    <FormControl>
      <InputLabel
        id="select-accession-number-label"
        sx={{
          color: !confirmedGene ? "text.disabled" : "text.primary",
        }}
      >
        Select Accession Number
      </InputLabel>
      <Select
        labelId="select-accession-number-label"
        id="select-accession-number"
        value={selectedAccessionIndex}
        disabled={!confirmedGene}
        label={'Select Accession Number'}
      >
        <MenuItem value={0} onClick={() => {
          setSelectedAccessionIndex('0');
          setAccessionNumber('');
        }}>Custom</MenuItem>
        <Divider/>
        {getAccessionsForSelectedGene().map((item, index) => (
          <MenuItem value={index + 1} key={index} onClick={() => {
            // Set the Accession number selected index so the dropdown retains which item we click on.
            setSelectedAccessionIndex((index + 1).toString())
            // Now we need to ensure that the selected accessionNumber populates our accessionNumber field.
            setAccessionNumber(getAccessionsForSelectedGene()[index]);
          }}>{item}</MenuItem>
        ))}
      </Select>
      {hasCustomAccessionNumber && renderCustomSequenceTextArea()}
    </FormControl>
  );

  const renderEulaAndSearchButtons = () => (
    <SearchButton searchFormValidated={confirmedSpecies && confirmedGene && confirmedAccession && (hasCustomAccessionNumber ? (customGeneSequence.length >= MIN_SEQUENCE_LENGTH) : true)} species={SUPPORTED_SPECIES[parseInt(selectedSpeciesIndex) || 0]?.species} gene={geneName} accessionNumber={accessionNumber} sequence={hasCustomAccessionNumber ? customGeneSequence : ""}/>
  );

  return (
    <Box paddingTop={8}>
      <Stack
        alignItems="center"  // Centers content horizontally
        justifyContent="center"  // Centers content vertically
        flex={1}
        textAlign="center"  // Ensures text within Typography is centered
        sx={{
          background: 'linear-gradient(to right, #2193b0, #6dd5ed)', // Left to right blue gradient
          width: "100%",  // Ensure it spans full width
          position:'relative'
        }}
        overflow={'hidden'}
      > 
        {/* Background DNA Icon */}
        <Box
          sx={{
            position: "absolute",
            top: "35%",
            left: "15%",
            transform: "translate(-50%, -50%) rotate(-30deg)", // Center & rotate
            fontSize: "350px", // Large size
            color: "rgba(255, 255, 255, 0.15)", // Translucent white
            pointerEvents: "none", // Prevent interaction
          }}
        >
          <FontAwesomeIcon icon={faDna} size="xl" />
        </Box>
        <Stack direction={'column'}  paddingTop={4} paddingBottom={4}>
          <Typography variant="h3" fontWeight="bold" color={"#FFFFFF"}>
            Design Therapeutic siRNAs
          </Typography>
          <Typography variant="body1" color={"#FFFFFF"}>
            For academic use only. Results and accuracy of designs are not guaranteed.
          </Typography>
        </Stack>
        <Divider/>
        {/* Form itself */}
        <Box paddingBottom={8} sx={{minWidth:{'xs':'100%', sm:'700px'}}}>
          <Paper elevation={10}>
            <Stack padding={1.5} gap={1}>
              <Stack flex={1}>{renderSelectSpecies()}</Stack>
              <Stack flex={1}>{renderSelectGene()}</Stack>
              <Stack flex={1}>{renderSelectAccessionNumber()}</Stack>
              <Stack flex={1}>{renderEulaAndSearchButtons()}</Stack>
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};

export default SearchForm;